import type { QueryObserverOptions } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import {
  CACHE_KEY_HOME_BANNER,
  CACHE_KEY_ORDER,
  CACHE_KEY_PHARMAKON_V3_ACTIVE_PENDING_MEDS,
} from 'src/app/hooks/data/_keys';
import { getErrorEventPayload } from 'src/app/hooks/data/_utils';
import { useBffClient } from 'src/app/hooks/data/useBffClient';
import { useUser } from 'src/app/hooks/data/user';
import useAuth from 'src/app/hooks/useAuth';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';
import type { Order } from 'src/app/types';

export const useGetOrder = (orderId?: string, options?: Omit<QueryObserverOptions<Order, AxiosError>, 'enabled'>) => {
  const trackErrorEvent = useSegmentEvent('error', { shouldTrackOnMount: false });
  const authClient = useBffClient();
  const { loggedInUser } = useAuth();
  const { data: user } = useUser(loggedInUser ? loggedInUser.uuid : '', Boolean(loggedInUser));
  const accountId = user?.accountId ?? '';

  return useQuery<Order, AxiosError>(
    [CACHE_KEY_ORDER, accountId, 'get', orderId],
    () =>
      authClient
        .get(`/v2/orders/${orderId}`, {
          headers: { tz: Intl.DateTimeFormat().resolvedOptions().timeZone, 'Capsule-Client': 'web' },
        })
        .then(resp => resp.data)
        .catch(error => {
          trackErrorEvent(getErrorEventPayload('GET Order Details Failed', error));
        }),
    {
      ...options,
      enabled: !!accountId && !!orderId,
    }
  );
};

export const useOrderCount = () => {
  const authClient = useBffClient();
  const { loggedInUser } = useAuth();
  const { data: user } = useUser(loggedInUser ? loggedInUser.uuid : '', Boolean(loggedInUser));
  const accountId = user?.accountId ?? '';

  return useQuery<number, AxiosError>(
    [CACHE_KEY_ORDER, accountId, 'count'],
    () =>
      authClient
        .get('/v2/orders/count', { headers: { tz: Intl.DateTimeFormat().resolvedOptions().timeZone } })
        .then(resp => resp.data.count as number),
    {
      refetchOnWindowFocus: false,
      enabled: !!accountId,
    }
  );
};

export const useOrderPatch = (orderId?: string | null) => {
  const queryClient = useQueryClient();
  const authClient = useBffClient();
  const { loggedInUser } = useAuth();
  const { data: user } = useUser(loggedInUser ? loggedInUser.uuid : '', Boolean(loggedInUser));
  const accountId = user?.accountId ?? '';

  return useMutation<Order, AxiosError, { deliveryWindowUUID: string }>(
    ({ deliveryWindowUUID }) =>
      authClient
        .patch(
          `/v2/orders/${orderId}`,
          { deliveryWindowUUID, accountId },
          { headers: { tz: Intl.DateTimeFormat().resolvedOptions().timeZone } }
        )
        .then(resp => resp.data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([CACHE_KEY_ORDER, accountId, 'get', orderId]);
        queryClient.invalidateQueries([CACHE_KEY_PHARMAKON_V3_ACTIVE_PENDING_MEDS]);
        queryClient.invalidateQueries([CACHE_KEY_HOME_BANNER]);
      },
    }
  );
};
